import { AlertTriangle, CheckCircle2, FileText, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { learningPaths } from "../data/questions";
import type { ImportJob, LearningPathId } from "../types";

interface AdminImportPanelProps {
  jobs: ImportJob[];
  onCreate: (input: { fileName: string; pathId: LearningPathId; markdown: string; sourceManifest: string[] }) => void;
}

export function AdminImportPanel({ jobs, onCreate }: AdminImportPanelProps) {
  const [fileName, setFileName] = useState("patg-iii-einspruch.pdf");
  const [pathId, setPathId] = useState<LearningPathId>("deutsche-ausbildung");
  const [sourceManifest, setSourceManifest] = useState("patg59, patg60, patg61");
  const [markdown, setMarkdown] = useState(`# PatG III - Einspruch

## Lernpfad
Deutsche Ausbildung / PatG III

## Normen
- § 59 PatG
- § 60 PatG

## Pruefungsschema
1. Zulaessigkeit des Einspruchs
2. Einspruchsgruende
3. Entscheidung und Rechtsmittel

## Potentielle Fragenanker
- Fristbeginn und Einspruchsfrist
- Einspruchsgruende und ihre Abgrenzung
- Wirkung der Ruecknahme`);

  const manifestItems = useMemo(
    () =>
      sourceManifest
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    [sourceManifest]
  );

  return (
    <section className="admin-panel" id="admin-import">
      <div className="panel-heading">
        <span className="eyebrow">Admin Import</span>
        <h2>PDF rein, Markdown plus Quellenmanifest raus.</h2>
        <p>
          Downstream-Agenten arbeiten nur mit Markdown, Quellenmanifest und Review-Status. Roh-PDFs bleiben auf den
          material-extractor begrenzt.
        </p>
      </div>

      <div className="import-layout">
        <form
          className="import-form"
          onSubmit={(event) => {
            event.preventDefault();
            onCreate({ fileName, pathId, markdown, sourceManifest: manifestItems });
          }}
        >
          <label>
            PDF-Dateiname
            <input onChange={(event) => setFileName(event.target.value)} value={fileName} />
          </label>
          <label>
            Lernpfad
            <select onChange={(event) => setPathId(event.target.value as LearningPathId)} value={pathId}>
              {learningPaths.map((path) => (
                <option key={path.id} value={path.id}>
                  {path.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Quellenmanifest
            <input onChange={(event) => setSourceManifest(event.target.value)} value={sourceManifest} />
          </label>
          <label className="markdown-label">
            Markdown-Arbeitsfassung
            <textarea onChange={(event) => setMarkdown(event.target.value)} value={markdown} />
          </label>
          <button className="primary-action" type="submit">
            <Upload size={18} />
            Import-Job anlegen
          </button>
        </form>

        <div className="job-list">
          {jobs.length ? (
            jobs.map((job) => (
              <article className="job-card" key={job.id}>
                <div>
                  <span className="job-status">{job.status}</span>
                  <h3>{job.markdownTitle}</h3>
                  <p>{job.fileName}</p>
                </div>
                {job.checks.length ? (
                  <ul className="check-list warning">
                    {job.checks.map((check) => (
                      <li key={check}>
                        <AlertTriangle size={15} />
                        {check}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="ready-note">
                    <CheckCircle2 size={16} />
                    Bereit fuer Question-Generation und Review.
                  </p>
                )}
                <details>
                  <summary>
                    <FileText size={16} />
                    Extraktionslog
                  </summary>
                  <ul>
                    {job.extractionLog.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </details>
              </article>
            ))
          ) : (
            <article className="job-card empty">
              <h3>Noch keine Import-Jobs</h3>
              <p>Der erste Sprint kann mit PatG III, EPU Core 01 oder dem naechsten Hagen-Block starten.</p>
            </article>
          )}
        </div>
      </div>
    </section>
  );
}
